// Plan 30-02 Task 1 — extractRuneThemeTokens helper.
//
// Reads the 11 --rune-* CSS-var entries from a validated ThemeDescriptor
// and returns a typed RuneThemeTokens struct ready for Pixi consumption
// (color ints + scalar float). LetterCubeSprite (Plan 30-02 Task 2)
// constructs ONCE per cell with these tokens; theme switches re-call this
// and pass the new tokens to updateCell().
//
// Pitfall 3 contract (RESEARCH §5 + UI-SPEC §3 token-presence gate): any
// missing key throws an Error naming the key. We do NOT silently default
// to black — silent black on a board cell is visually identical to "tint
// is loading" and the user has no way to tell the manifest is broken.
//
// Why we parse here instead of trusting isValidThemeDescriptor's check:
// the validator returns boolean; this returns the strongly-typed shape the
// sprite wants. Validator + extractor are complementary — validator is the
// outer gate (load-time), extractor is the parse-and-typed-shape step.
//
// The 11th token (--rune-affinity-halo-opacity) is a scalar float string;
// the other 10 are hex colors (#RRGGBB). We parseFloat the scalar and
// hex-decode the others.

import type { ThemeDescriptor } from './descriptor'

/**
 * Typed runtime view of the 11 --rune-* theme tokens. Colors are 0xRRGGBB
 * 24-bit ints (the Pixi `Sprite.tint` / `Graphics.fill({color})` format);
 * opacity is a float in [0..1].
 */
export interface RuneThemeTokens {
  letterTintPlayerA: number
  letterTintPlayerB: number
  tierForgedRingColor: number
  tierRareInlayColor: number
  tierMythicGlowColor: number
  tierMythicSparkleColor: number
  anchorSigilColorPlayerA: number
  anchorSigilColorPlayerB: number
  hollowCrackColor: number
  affinityHaloColor: number
  affinityHaloOpacity: number
}

const REQUIRED_HEX_KEYS = [
  '--rune-letter-tint-player-a',
  '--rune-letter-tint-player-b',
  '--rune-tier-forged-ring-color',
  '--rune-tier-rare-inlay-color',
  '--rune-tier-mythic-glow-color',
  '--rune-tier-mythic-sparkle-color',
  '--rune-anchor-sigil-color-player-a',
  '--rune-anchor-sigil-color-player-b',
  '--rune-hollow-crack-color',
  '--rune-affinity-halo-color',
] as const

const SCALAR_OPACITY_KEY = '--rune-affinity-halo-opacity' as const

/**
 * Decode a '#RRGGBB' hex string into a 24-bit integer (0xRRGGBB). The
 * leading '#' is required (matches manifest format); throws if the string
 * is malformed.
 */
function hexToInt(hex: string, keyForError: string): number {
  if (!/^#[0-9A-Fa-f]{6}$/.test(hex)) {
    throw new Error(
      `extractRuneThemeTokens: malformed hex value for '${keyForError}' — expected '#RRGGBB', got '${hex}'`,
    )
  }
  return parseInt(hex.replace('#', ''), 16)
}

/**
 * Extract the 11 Runespring theme tokens into a typed struct. Throws on
 * any missing key (Pitfall 3 fail-loud contract) — the error message
 * names the specific token so a misconfigured manifest is debuggable
 * from the stack trace alone.
 */
export function extractRuneThemeTokens(
  descriptor: ThemeDescriptor,
): RuneThemeTokens {
  // The validator narrows css_vars to include the rune keys as optional;
  // we re-cast to a plain record for the loop below.
  const cv = descriptor.css_vars as Record<string, string | undefined>

  // Presence gate — fail loud naming the missing key.
  for (const key of REQUIRED_HEX_KEYS) {
    if (typeof cv[key] !== 'string') {
      throw new Error(
        `extractRuneThemeTokens: missing token '${key}' in theme manifest css_vars`,
      )
    }
  }
  if (typeof cv[SCALAR_OPACITY_KEY] !== 'string') {
    throw new Error(
      `extractRuneThemeTokens: missing token '${SCALAR_OPACITY_KEY}' in theme manifest css_vars`,
    )
  }

  // After the presence loop above, cv[key] is non-undefined for each of
  // these keys — non-null assertion is type-narrowing, not unsafe.
  const opacity = parseFloat(cv[SCALAR_OPACITY_KEY] as string)
  if (!Number.isFinite(opacity)) {
    throw new Error(
      `extractRuneThemeTokens: '${SCALAR_OPACITY_KEY}' is not a finite number (got '${cv[SCALAR_OPACITY_KEY]}')`,
    )
  }

  return {
    letterTintPlayerA: hexToInt(
      cv['--rune-letter-tint-player-a'] as string,
      '--rune-letter-tint-player-a',
    ),
    letterTintPlayerB: hexToInt(
      cv['--rune-letter-tint-player-b'] as string,
      '--rune-letter-tint-player-b',
    ),
    tierForgedRingColor: hexToInt(
      cv['--rune-tier-forged-ring-color'] as string,
      '--rune-tier-forged-ring-color',
    ),
    tierRareInlayColor: hexToInt(
      cv['--rune-tier-rare-inlay-color'] as string,
      '--rune-tier-rare-inlay-color',
    ),
    tierMythicGlowColor: hexToInt(
      cv['--rune-tier-mythic-glow-color'] as string,
      '--rune-tier-mythic-glow-color',
    ),
    tierMythicSparkleColor: hexToInt(
      cv['--rune-tier-mythic-sparkle-color'] as string,
      '--rune-tier-mythic-sparkle-color',
    ),
    anchorSigilColorPlayerA: hexToInt(
      cv['--rune-anchor-sigil-color-player-a'] as string,
      '--rune-anchor-sigil-color-player-a',
    ),
    anchorSigilColorPlayerB: hexToInt(
      cv['--rune-anchor-sigil-color-player-b'] as string,
      '--rune-anchor-sigil-color-player-b',
    ),
    hollowCrackColor: hexToInt(
      cv['--rune-hollow-crack-color'] as string,
      '--rune-hollow-crack-color',
    ),
    affinityHaloColor: hexToInt(
      cv['--rune-affinity-halo-color'] as string,
      '--rune-affinity-halo-color',
    ),
    affinityHaloOpacity: opacity,
  }
}
